import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Icon, Label, Loader, Popup } from "semantic-ui-react";

import moment from "moment";
import { useIntl } from "react-intl";
import { ComponentPropsGetterR } from "react-table-6";

import DeleteColumn from "react-lib/appcon/common/DeleteColumn";

import CardLabOrderListUX from "./CardLabOrderListUX";
import CardLabRequest, { CardLabRequestProps } from "./CardLabRequest";
import SubLabCareProvider from "./SubLabCareProvider";

import { checkAllowEditOrder } from "./sequence/OrderCentralLab";

import { beToAd, formatDate, formatDatetime } from "react-lib/utils/dateUtils";

type CardLabOrderListProps = CardLabRequestProps & {
  doctorLabOrderLoading?: boolean;
  setEditOrder?: (value: Record<string, any> | null) => void;
  setIsLabRequested?: (value: boolean) => void;
  setOrderType?: (value: string) => void;
};

const BUTTON_ACTIONS = {
  edit: "EDIT",
};

const CARD_LAB_REQUEST = "CardLabRequest";
const CARD_LAB_ORDER_LIST = "CardLabOrderList";

const ACTION_EDIT = `${CARD_LAB_ORDER_LIST}_${BUTTON_ACTIONS.edit}`;

const CardLabOrderList = (props: CardLabOrderListProps) => {
  const intl = useIntl();

  const [filterCreateDate, setFilterCreateDate] = useState(() => formatDate(moment()));
  const [isLabRequested, setIsLabRequested] = useState<boolean>(false);
  const [dataChanged, setDataChanged] = useState<boolean>(false);

  useEffect(() => {
    props.setIsLabRequested?.(isLabRequested);
  }, [isLabRequested]);

  useEffect(() => {
    if (props.OrderCentralLabSequence?.labOrder?.id) {
      setIsLabRequested(true);

      props.setOrderType?.(props.OrderCentralLabSequence.labOrder.order_type);
    }
  }, [props.OrderCentralLabSequence?.labOrder?.id]);

  const handleEdit = useCallback(
    async (index: number) => {
      const items: any[] = props.doctorLabOrderList?.items ?? [];

      if (props.setEditOrder) {
        const id = items[index].id;

        const detail = await props.onEvent({
          message: "GetDoctorPendingTaskOrder",
          params: { id, btnAction: `${ACTION_EDIT}_${id}` },
        });

        props.setEditOrder(detail || items[index]);
      }

      setDataChanged(false);
      setIsLabRequested(true);

      props.runSequence({
        sequence: "OrderCentralLab",
        action: "edit",
        isNullEmr: props.isNullEmr,
        selected: items[index].id || null,
      });
    },
    [props.doctorLabOrderList, props.isNullEmr, props.setEditOrder]
  );

  const handleDelete = useCallback(
    (index: number, note: string) => {
      const items: any[] = props.doctorLabOrderList?.items ?? [];

      props.setProp("OrderCentralLabSequence.labOrder", {});

      props.runSequence({
        sequence: "OrderCentralLab",
        action: "cancel",
        isNullEmr: props.isNullEmr,
        item: items[index],
        note,
      });
    },
    [props.doctorLabOrderList, props.isNullEmr]
  );

  const formattedItems = useMemo(
    () =>
      (props.doctorLabOrderList?.items ?? []).map((item, index) => {
        const roleTypes: string[] = props.django?.user?.role_types ?? [];
        const isTechnician = roleTypes.includes("TECHNICIAN");
        const isNurse = roleTypes.includes("REGISTERED_NURSE");
        const isDoctor = roleTypes.includes("DOCTOR");
        const isVerbalOrder = item.order_type_name !== "DIRECT" && !item.order_approve_time;
        const isCosigned = item.order_type_name !== "DIRECT" && !!item.order_approve_time;
        const orderStatus: string = item.order_status || "";

        const getDisabledButton = () => {
          if (orderStatus === "CANCEL") {
            return true;
          } else if (item.system === "bloodbank") {
            return false;
          } else if (orderStatus === "PENDING" && isCosigned) {
            return isDoctor ? false : isNurse;
          } else if (orderStatus === "PERFORMED") {
            return !isTechnician;
          }

          return false;
        };

        const hasPermission = (): boolean => {
          return (
            !props.isStudentUser ||
            item.order_by !== item.approved_by ||
            item.order_by === item.student
          );
        };

        const disabledButton = getDisabledButton();

        const canEditOrder = hasPermission();
        const canDeleteOrder = hasPermission();

        return {
          ...item,
          allowedEdit: canEditOrder && !disabledButton,
          careprovider: (
            <SubLabCareProvider
              setProp={props.setProp}
              runSequence={props.runSequence}
              cardKey={CARD_LAB_REQUEST}
              item={item}
              selectedEmr={props.selectedEmr}
              buttonLoadCheck={props.buttonLoadCheck}
              django={props.django}
              errorMessage={props.errorMessage}
              masterOptions={props.masterOptions}
              successMessage={props.successMessage}
              onApprove={() => {
                setDataChanged(false);
              }}
            />
          ),
          created: formatDatetime(item.created, true),
          del: canDeleteOrder && (
            <DeleteColumn
              disabled={disabledButton || orderStatus === "PERFORMED"}
              icon="trash alternate"
              size="mini"
              titleColor="red"
              options={props.masterOptions?.labCancelReason}
              onConfirm={(note) => {
                handleDelete(index, note);
              }}
              titleName={intl.formatMessage({
                id: "ต้องการลบ Lab Center Laboratory นี้ใช่หรือไม่",
              })}
            />
          ),
          detail: (
            <div
              style={{
                textDecoration: item.order_status === "CANCEL" ? "line-through" : "",
              }}
              dangerouslySetInnerHTML={{
                __html: item.order_status === "CANCEL" ? item.cancel_detail : item.detail,
              }}
            />
          ),
          edit: canEditOrder && (
            <div style={{ display: "grid", placeContent: "center" }}>
              <Button
                color="blue"
                disabled={disabledButton}
                icon="edit alternate"
                loading={props.buttonLoadCheck?.[`${ACTION_EDIT}_${item.id}`]}
                size="mini"
                onClick={() => {
                  void handleEdit(index);
                }}
              />
            </div>
          ),
          full_access_number: (
            <div>
              {item.full_access_number}
              {(isVerbalOrder || isCosigned) && (
                <Popup
                  style={{ border: "1px solid var(--primary-theme-color)" }}
                  position="bottom left"
                  content={
                    <>
                      <strong>ผู้บันทึกรับคำสั่ง</strong>
                      <br />
                      {item.order_receive_by_name}
                    </>
                  }
                  trigger={
                    <Label
                      style={{ marginLeft: "0.2rem" }}
                      color={isVerbalOrder ? "yellow" : "green"}
                      size="tiny"
                    >
                      {isVerbalOrder ? "รคส." : "รคส. (co-signed)"}
                    </Label>
                  }
                />
              )}
            </div>
          ),
          order_status: (
            <div style={{ display: "flex" }}>
              <div style={{ marginRight: "0.5rem", textTransform: "capitalize" }}>
                {orderStatus.toLowerCase()}
              </div>
              {item.cancel_dict?.note && (
                <Popup
                  style={{ border: "1px solid var(--primary-theme-color)" }}
                  position="bottom left"
                  trigger={<Icon color="red" name="info circle" />}
                  content={
                    <div>
                      <div style={{ minWidth: "max-content" }}>
                        <label>{item.cancel_dict.user_full_name}</label>
                        <label style={{ paddingLeft: "1rem" }}>
                          {formatDatetime(item.cancel_dict.datetime, true)}
                        </label>
                      </div>
                      <div style={{ color: "red" }}>{item.cancel_dict.note}</div>
                    </div>
                  }
                />
              )}
              {(item.note_list ?? []).length > 0 && (
                <Popup
                  style={{ border: "1px solid var(--primary-theme-color)" }}
                  position="bottom left"
                  trigger={<Icon style={{ color: "#0147A3" }} name="info circle" />}
                  content={() => {
                    return item.note_list?.map((noteItem: any, noteIndex: number) => (
                      <div key={noteIndex}>
                        <div style={{ minWidth: "max-content" }}>
                          <label>{noteItem?.user_fullname || ""}</label>
                          <label style={{ paddingLeft: "1rem" }}>{noteItem?.datetime || ""}</label>
                        </div>
                        <div style={{ color: "#0147A3" }}>{noteItem?.note || ""}</div>
                      </div>
                    ));
                  }}
                />
              )}
            </div>
          ),
        };
      }),
    [
      handleEdit,
      props.buttonLoadCheck,
      props.currentDoctor,
      props.django,
      props.doctorLabOrderList,
      props.isNullEmr,
      props.masterOptions?.labCancelReason,
    ]
  );

  const handleChangeFilterDate = (data: { date?: string; type?: "next" | "prev" }) => {
    const { date: inputDate, type } = data;

    let date = inputDate || filterCreateDate;

    // ตรวจสอบว่า date มีค่าและมีรูปแบบที่ถูกต้อง
    if (!type && !/\b\d{2}\/\d{2}\/\d{4}\b/.test(inputDate || "")) {
      return;
    }

    // ใช้ beToAd แปลงวันที่และเพิ่มหรือลดวันตาม type
    const formattedDate = beToAd(date || "")?.add(type === "next" ? 1 : -1, "days");

    if (type) {
      date = formatDate(formattedDate);
    }

    // ตรวจสอบวการแปลงวัน
    if (formattedDate) {
      setFilterCreateDate(date);

      handleRefresh(date);
    } else {
      console.error("Failed to format the date.");
    }
  };

  const handleRefresh = (date: string) => {
    props.setProp("OrderCentralLabSequence.labOrder", {});

    props.onEvent({
      message: "GetDoctorLabOrderList",
      params: {
        emr: props.selectedEmr?.id || null,
        orderDate: date,
        // progression_cycle: props.selectedProgressCycle?.id || null,
      },
    });
  };

  const handleAddLabRequest = () => {
    props.setProp("OrderCentralLabSequence.labOrder", {});
    props.setEditOrder?.(null);
    props.setOrderType?.("");

    setIsLabRequested(true);
  };

  const handleSuccess = () => {
    props.setProp("OrderCentralLabSequence.labOrder", {});

    setIsLabRequested(false);
  };

  const handleCancel = () => {
    setIsLabRequested(false);
  };

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    const { original } = rowInfo ?? {};
    const id = props.OrderCentralLabSequence?.labOrder?.id;

    return {
      className: id && id === original?.id ? "blueSelectedRow" : "",
      onClick: () => {
        if (typeof rowInfo?.index === "number" && original.allowedEdit) {
          void handleEdit(rowInfo.index);
        }
      },
    };
  };

  console.log("");

  return (
    <div>
      <CardLabOrderListUX
        filterCreateDate={filterCreateDate}
        items={formattedItems}
        loader={props.doctorLabOrderLoading ? <Loader active> </Loader> : null}
        getTrProps={handleGetTrProps}
        onAddLabRequest={handleAddLabRequest}
        onChangeFilterDate={handleChangeFilterDate}
        noDataComponent={() =>
          props.doctorLabOrderLoading ? null : (
            <div className="rt-noData">{intl.formatMessage({ id: "ไม่พบข้อมูล" })}</div>
          )
        }
      />
      <CardLabRequest
        onEvent={props.onEvent}
        setProp={props.setProp}
        runSequence={props.runSequence}
        OrderCentralLabSequence={props.OrderCentralLabSequence}
        OrderSetTemplateSequence={props.OrderSetTemplateSequence}
        currentDoctor={props.currentDoctor}
        dataChanged={dataChanged}
        divisionType={props.divisionType}
        doctorLabOrderList={props.doctorLabOrderList}
        doctorLabOrderTableStyle={{ display: "none" }}
        editId={props.editId}
        hideActionButton={props.hideActionButton}
        hideLabRequest={!isLabRequested}
        hidePreOrder={props.hidePreOrder}
        isStudentUser={props.isStudentUser}
        medReconcileCheck={props.medReconcileCheck}
        medReconcileIndex={props.medReconcileIndex}
        openModTechnicianNote={props.openModTechnicianNote}
        orderDate={filterCreateDate}
        saveLabOrderStyle={props.saveLabOrderStyle}
        searchedItemList={props.searchedItemList}
        selectedEmr={props.selectedEmr}
        selectedProgressCycle={props.selectedProgressCycle}
        adminMenuDivisions={props.adminMenuDivisions}
        buttonLoadCheck={props.buttonLoadCheck}
        django={props.django}
        errorMessage={props.errorMessage}
        languageUX={props.languageUX}
        masterData={props.masterData}
        masterOptions={props.masterOptions}
        selectedDivision={props.selectedDivision}
        selectedEncounter={props.selectedEncounter}
        successMessage={props.successMessage}
        supplyOrderEligibilityOptions={props.supplyOrderEligibilityOptions}
        onCancel={handleCancel}
        onSaveVerbalOrder={props.onSaveVerbalOrder}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

CardLabOrderList.displayName = "CardLabOrderList";

export default React.memo(CardLabOrderList);
